import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, PackageCheck, FlaskConical, ShieldAlert, ArrowRight } from "lucide-react";

// Full PO -> GRN -> Receipt -> QA flow. Stock is only affected at QA-accept,
// which is gated behind a strong confirmation.
export function GrnReceiveDialog({ open, onOpenChange, po, onDone }) {
  const [step, setStep] = useState("create"); // create | receipt | qa
  const [grn, setGrn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState({});
  const [qa, setQa] = useState({});
  const [qaConfirm, setQaConfirm] = useState(false);

  useEffect(() => {
    if (open) { setStep("create"); setGrn(null); setReceipt({}); setQa({}); }
  }, [open]);

  const grnLines = grn ? (grn.lines || grn.items || []) : [];

  const createGrn = async () => {
    setBusy(true);
    try {
      const res = await api.post("/grns", { poId: po.id });
      const g = res.data;
      setGrn(g);
      const lines = g.lines || g.items || [];
      const rInit = {};
      lines.forEach((l) => { rInit[l.id] = { receivedQty: l.orderedQty ?? l.qty ?? "", batchNo: "", expiryDate: "", rate: l.rate ?? "" }; });
      setReceipt(rInit);
      setStep("receipt");
      toast.success("GRN created (no stock affected yet)");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not create GRN"));
    } finally { setBusy(false); }
  };

  const saveReceipt = async () => {
    setBusy(true);
    try {
      const lines = grnLines.map((l) => {
        const r = receipt[l.id] || {};
        const line = { id: l.id, receivedQty: Number(r.receivedQty) || 0, batchNo: r.batchNo || "" };
        if (r.expiryDate) line.expiryDate = r.expiryDate;
        if (r.rate !== "" && r.rate !== undefined && r.rate !== null) line.rate = Number(r.rate);
        return line;
      });
      await api.put(`/grns/${grn.id}/receipt`, { lines });
      const qInit = {};
      grnLines.forEach((l) => { qInit[l.id] = { acceptedQty: Number(receipt[l.id]?.receivedQty) || 0, rejectedQty: 0, rejectReason: "" }; });
      setQa(qInit);
      setStep("qa");
      toast.success("Receipt recorded");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save receipt"));
    } finally { setBusy(false); }
  };

  const postQa = async () => {
    setQaConfirm(false);
    setBusy(true);
    try {
      const lines = grnLines.map((l) => ({
        id: l.id,
        acceptedQty: Number(qa[l.id]?.acceptedQty) || 0,
        rejectedQty: Number(qa[l.id]?.rejectedQty) || 0,
        rejectReason: qa[l.id]?.rejectReason || "",
      }));
      await api.post(`/grns/${grn.id}/qa`, { lines });
      toast.success("QA posted — accepted quantities added to inventory ledger");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, "QA posting failed"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <PackageCheck className="h-5 w-5 text-amber-700" /> Receive against {po?.poNumber}
            </DialogTitle>
            <DialogDescription className="text-xs">PO → GRN → Receipt → QA. Stock changes only at QA-accept, via the backend ledger.</DialogDescription>
          </DialogHeader>

          <div className="mb-2 flex items-center gap-2 text-xs">
            {["create", "receipt", "qa"].map((s, i) => (
              <span key={s} className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 font-medium ${step === s ? "bg-amber-700 text-white" : "bg-stone-100 text-stone-500"}`}>
                  {i + 1}. {s === "create" ? "Create GRN" : s === "receipt" ? "Receipt" : "QA"}
                </span>
                {i < 2 && <ArrowRight className="h-3 w-3 text-stone-300" />}
              </span>
            ))}
          </div>

          {step === "create" && (
            <div className="space-y-4">
              <p className="text-sm text-stone-600">This creates a Goods Receipt Note linked to <b>{po?.poNumber}</b> ({po?.supplierName}). Creating the GRN does <b>not</b> change any stock.</p>
              <div className="flex justify-end">
                <Button data-testid="grn-create" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={createGrn}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} Create GRN
                </Button>
              </div>
            </div>
          )}

          {step === "receipt" && (
            <div className="space-y-3">
              <div className="space-y-2">
                {grnLines.map((l) => (
                  <div key={l.id} className="rounded-lg border border-stone-200 p-3">
                    <div className="mb-2 text-sm font-medium text-stone-800">{l.name || l.ingredientName} <span className="text-xs text-stone-400">(ordered {fmtNumber(l.orderedQty ?? l.qty)} {l.unit})</span></div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div><Label className="text-[11px]">Received Qty</Label><Input data-testid={`grn-recv-${l.id}`} type="number" className="h-8 text-sm" value={receipt[l.id]?.receivedQty ?? ""} onChange={(e) => setReceipt((s) => ({ ...s, [l.id]: { ...s[l.id], receivedQty: e.target.value } }))} /></div>
                      <div><Label className="text-[11px]">Batch/Lot</Label><Input className="h-8 text-sm" value={receipt[l.id]?.batchNo ?? ""} onChange={(e) => setReceipt((s) => ({ ...s, [l.id]: { ...s[l.id], batchNo: e.target.value } }))} /></div>
                      <div><Label className="text-[11px]">Expiry</Label><Input type="date" className="h-8 text-sm" value={receipt[l.id]?.expiryDate ?? ""} onChange={(e) => setReceipt((s) => ({ ...s, [l.id]: { ...s[l.id], expiryDate: e.target.value } }))} /></div>
                      <div><Label className="text-[11px]">Rate</Label><Input type="number" className="h-8 text-sm" value={receipt[l.id]?.rate ?? ""} onChange={(e) => setReceipt((s) => ({ ...s, [l.id]: { ...s[l.id], rate: e.target.value } }))} /></div>
                    </div>
                  </div>
                ))}
                {grnLines.length === 0 && <p className="text-sm text-muted-foreground">GRN has no lines.</p>}
              </div>
              <DialogFooter>
                <Button data-testid="grn-save-receipt" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={saveReceipt}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Receipt & Continue to QA"}
                </Button>
              </DialogFooter>
            </div>
          )}

          {step === "qa" && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                Posting QA is the step that <b>adds accepted quantities to the inventory ledger</b>. Rejected/quarantined quantities do NOT become available stock.
              </div>
              {grnLines.map((l) => (
                <div key={l.id} className="rounded-lg border border-stone-200 p-3">
                  <div className="mb-2 text-sm font-medium text-stone-800">{l.name || l.ingredientName}</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-[11px]">Accepted</Label><Input data-testid={`grn-accept-${l.id}`} type="number" className="h-8 text-sm" value={qa[l.id]?.acceptedQty ?? ""} onChange={(e) => setQa((s) => ({ ...s, [l.id]: { ...s[l.id], acceptedQty: e.target.value } }))} /></div>
                    <div><Label className="text-[11px]">Rejected</Label><Input type="number" className="h-8 text-sm" value={qa[l.id]?.rejectedQty ?? ""} onChange={(e) => setQa((s) => ({ ...s, [l.id]: { ...s[l.id], rejectedQty: e.target.value } }))} /></div>
                    <div><Label className="text-[11px]">Reject Reason</Label><Input className="h-8 text-sm" value={qa[l.id]?.rejectReason ?? ""} onChange={(e) => setQa((s) => ({ ...s, [l.id]: { ...s[l.id], rejectReason: e.target.value } }))} /></div>
                  </div>
                </div>
              ))}
              <DialogFooter>
                <Button data-testid="grn-post-qa" className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => setQaConfirm(true)}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} Post QA (affects stock)
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={qaConfirm} onOpenChange={setQaConfirm}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-rose-600" /> Confirm inventory receipt</AlertDialogTitle>
            <AlertDialogDescription>This posts QA and creates inventory ledger entries for accepted quantities in the live ERP. This cannot be silently undone. Proceed?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="grn-qa-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="grn-qa-confirm" className="bg-emerald-600 hover:bg-emerald-700" onClick={postQa}>Yes, post to inventory</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
